<ng-container *transloco="let t; prefix: 'field-view'"
  ><div class="content-padding">
    @if (isLoading) {
      <div class="xi-loader-container__center">
        <xi-animation [loop]="true" animation="cx_loader"></xi-animation>
      </div>
    }
    @if (!isLoading) {
      <div class="field-view__page-header">
        <div class="field-view__page-header-title">
          <h1
            class="kxi-text-xlarge kxi-text-600"
            [matTooltip]="field | sideloadedLabel: 'xiFieldLabels'"
          >
            {{ field | sideloadedLabel: 'xiFieldLabels' }}
          </h1>
          @if (field.locked) {
            <xi-icon icon="password_filled" [tooltipProps]="{ text: t('locked-field') }"></xi-icon>
          }
          @if (field.groupId === GLOBAL) {
            <xi-icon icon="globe" [tooltipProps]="{ text: t('global-field') }"></xi-icon>
          }
        </div>
        @if (fieldDeleted) {
          <h2 class="kxi-text-large kxi-text-600">{{ t('field-deleted') }}</h2>
        }
        <div class="field-view__page-header-actions">
          @if (!fieldDeleted) {
            <div class="field-view__reload-button">
              <xi-icon-button
                buttonSize="large"
                icon="refresh"
                [tooltip]="{ text: t('refresh') }"
                (onClick)="onReloadField()"
              ></xi-icon-button>
            </div>
            @if (!isReadOnly) {
              <xi-icon-button
                [disabled]="!!field.documentCount || field.locked || aliasesTableData.data?.length"
                [tooltip]="{ text: deleteTooltip, direction: 9 }"
                buttonSize="large"
                icon="trash"
                (onClick)="onDeleteField()"
              ></xi-icon-button>
            }
          }
        </div>
      </div>
      <form [formGroup]="fieldForm" class="field-view__cards-container">
        <section class="field-view__card">
          <div class="field-view__section-header">
            <h5>{{ t('details') }}</h5>
          </div>
          <div class="field-view__details-form">
            <div class="field-view__input-wrapper">
              <label for="field-view__name">{{ t('name') }}</label>
              <xi-input-field
                inputId="field-view__name"
                formControlName="label"
                [status]="fieldLabelStatus"
                [readOnly]="!field.alias || readonlyFields['label']"
                [matTooltip]="
                  !field.alias
                    ? t('cannot-change-name')
                    : field.locked
                      ? t('cannot-change-name-locked')
                      : null
                "
              ></xi-input-field>
            </div>
            <div class="field-view__input-wrapper">
              <label for="field-view__long-name">{{ t('long-name') }}</label>
              <xi-input-field
                inputId="field-view__long-name"
                formControlName="description"
                [readOnly]="readonlyFields['description']"
                [status]="fieldDescriptionStatus"
              ></xi-input-field>
            </div>
            <div class="field-view__input-wrapper">
              <label for="field-view__type">{{ t('type') }}</label>
              <xi-dropdown
                [data]="fieldTypes"
                inputId="field-view__type"
                formControlName="type"
                [readOnly]="hasDetails || readonlyFields['type']"
                [tooltip]="{ text: hasDetails && !field.alias ? t('cannot-change-type') : null }"
                [stretch]="true"
              ></xi-dropdown>
            </div>
            @if (field.alias) {
              <div class="field-view__input-wrapper">
                <label for="field-view__subtype">{{ t('subtype') }}</label>
                <xi-dropdown
                  [readOnly]="true"
                  [data]="standardTypes"
                  inputId="field-view__subtype"
                  formControlName="subType"
                  [stretch]="true"
                ></xi-dropdown>
              </div>
            }
            <div class="field-view__input-wrapper">
              <label for="field-view__filterable">{{ t('filterable') }}</label>
              <xi-toggle-switch
                id="field-view__filterable"
                type="button"
                formControlName="filterable"
                leftText="{{ t('no') }}"
                rightText="{{ t('yes') }}"
              ></xi-toggle-switch>
            </div>

            @if (shouldShowUseAsIdField) {
              <div class="field-view__input-wrapper">
                <label for="field-view__id-field">{{ t('use-as-id-field') }}</label>
                <xi-toggle-switch
                  id="field-view__id-field"
                  type="button"
                  formControlName="identityField"
                  [matTooltip]="identityFieldDisabledTooltip"
                  leftText="{{ t('no') }}"
                  rightText="{{ t('yes') }}"
                ></xi-toggle-switch>
              </div>
            }
            @if (shouldShowRating) {
              <div class="field-view__input-wrapper">
                <label for="field-view__rating">{{ t('rating') }}</label>
                <xi-toggle-switch
                  type="button"
                  id="field-view__rating"
                  formControlName="rating"
                  [matTooltip]="ratingTooltip"
                  leftText="{{ t('no') }}"
                  rightText="{{ t('yes') }}"
                ></xi-toggle-switch>
              </div>
            }
            @if (shouldShowWeight) {
              <div class="field-view__input-wrapper">
                <label for="field-view__weight">{{ t('weight') }}</label>
                <xi-toggle-switch
                  type="button"
                  id="field-view__weight"
                  formControlName="weight"
                  [matTooltip]="weightTooltip"
                  leftText="{{ t('no') }}"
                  rightText="{{ t('yes') }}"
                ></xi-toggle-switch>
              </div>
            }
            @if (shouldShowRedacted) {
              <div class="field-view__input-wrapper">
                <label for="field-view__redacted">{{ t('contains-pii') }}</label>
                <xi-toggle-switch
                  type="button"
                  id="field-view__redacted"
                  formControlName="redacted"
                  [matTooltip]="redactedFieldTooltip"
                  leftText="{{ t('no') }}"
                  rightText="{{ t('yes') }}"
                ></xi-toggle-switch>
              </div>
            }
            @if (shouldShowRedactionBehavior) {
              <div class="field-view__input-wrapper">
                <label for="field-view__redactionBehavior">{{ t('pii-behavior') }}</label>
                <xi-dropdown
                  [data]="fieldRedactionBehaviors"
                  inputId="field-view__redactionBehavior"
                  formControlName="redactionBehavior"
                  [readOnly]="!canEditRedactionBehavior || readonlyFields['redactionBehavior']"
                  [stretch]="true"
                ></xi-dropdown>
              </div>
            }
            <div class="field-view__input-wrapper">
              <label for="field-view__locked">{{ t('locked-field') }}</label>
              <xi-toggle-switch
                type="button"
                id="field-view__locked"
                formControlName="locked"
                leftText="{{ t('no') }}"
                rightText="{{ t('yes') }}"
              ></xi-toggle-switch>
            </div>
          </div>
        </section>
        @if (isCalculatedField && field.groupId !== GLOBAL) {
          <section class="field-view__card">
            <div class="field-view__section-header">
              <div class="field-view__section-header--info">
                <h5>
                  {{ t('calculation') }}
                </h5>
                <xi-icon
                  icon="info"
                  color="CX"
                  [tooltipProps]="{ text: t('calculation-info'), direction: 11 }"
                ></xi-icon>
              </div>
            </div>
            <div class="field-view__details-form">
              <div class="field-view__input-wrapper">
                <label for="field-view__calcType">{{ t('calculation-type') }}</label>
                <xi-dropdown
                  formControlName="calcType"
                  [readOnly]="readonlyFields['calcType']"
                  inputId="field-view__calcType"
                  [data]="calcTypes"
                  [stretch]="true"
                ></xi-dropdown>
              </div>
              <div class="field-view__input-wrapper">
                <label for="field-view__calcDef">{{ t('calculation-definition') }}</label>
                <xi-text-area
                  formControlName="calcDef"
                  [readOnly]="readonlyFields['calcDef']"
                  inputId="field-view__calcDef"
                  type="text"
                  class="field-view__calc-def"
                  [status]="calcDefStatus"
                >
                </xi-text-area>
              </div>
            </div>
          </section>
        }
        @if (field.alias) {
          <section class="field-view__card">
            <div class="field-view__section-header">
              <h5>{{ t('aliased-metadata-fields') }}</h5>
            </div>
            <app-field-alias-selector
              [fields]="allFields"
              [selectedAliasedFields]="selectedAliasedFields"
              [newAlias]="false"
              [subType]="selectedSubType"
              [isReadOnly]="isReadOnly || this.isLocked"
              (selectionChanged)="onAliasSelectionChange($event)"
            ></app-field-alias-selector>
          </section>
        }

        @if (showBuckets) {
          <section class="field-view__card">
            <div class="field-view__section-header--info">
              <h5>{{ t('buckets') }}</h5>
              <xi-icon
                icon="info"
                color="CX"
                [tooltipProps]="{
                  text: t('bucket-info-tooltip'),
                }"
              ></xi-icon>
            </div>
            <div class="field-view__bucket-table-wrapper">
              <app-field-buckets
                [expectedType]="expectedType()"
                [buckets]="field.buckets"
                (fieldBuckets)="onFieldBucketChange($event)"
                [isReadOnly]="isReadOnly || this.isLocked"
              ></app-field-buckets>
            </div>
          </section>
        }
        @if (field.groupId !== GLOBAL) {
          <section class="field-view__card">
            <div class="field-view__section-header">
              <h5>{{ t('active-use') }}</h5>
            </div>
            <div class="field-view__table_wrapper">
              <table
                mat-table
                [dataSource]="activeUseTableData"
                class="xi-table__table field-view__active-use-table"
              >
                <ng-container matColumnDef="projectName">
                  <th mat-header-cell *matHeaderCellDef>
                    <div class="xi-table__table-header-content">
                      <div class="xi-table__table-header-border"></div>
                      <span>{{ t('project') }}</span>
                    </div>
                  </th>
                  <td
                    mat-cell
                    *matCellDef="let project"
                    [matTooltip]="project[0].projectName"
                    [matTooltipDisabled]="
                      project[0].projectName.length <= PROJECT_NAME_CHARACTER_LIMIT
                    "
                  >
                    <xi-default-button
                      fill="link"
                      [hasAnimation]="false"
                      [text]="project[0].projectName | truncate: PROJECT_NAME_CHARACTER_LIMIT"
                      (onClick)="onGoToCollection(project[0])"
                    ></xi-default-button>
                  </td>
                </ng-container>
                <ng-container matColumnDef="collectionName">
                  <th mat-header-cell *matHeaderCellDef>
                    <div class="xi-table__table-header-content">
                      <div class="xi-table__table-header-border"></div>
                      <span>{{ t('collection') }}</span>
                    </div>
                  </th>
                  <td mat-cell *matCellDef="let project">
                    @for (collection of project; track collection) {
                      <div>
                        <ng-container
                          *appMiddleEllipsize="collection.collectionName"
                        ></ng-container>
                      </div>
                    }
                  </td>
                </ng-container>
                <ng-container matColumnDef="documentCount">
                  <th mat-header-cell *matHeaderCellDef>
                    <div class="xi-table__table-header-content">
                      <div class="xi-table__table-header-border"></div>
                      <span>{{ t('doc-count') }}</span>
                    </div>
                  </th>
                  <td mat-cell *matCellDef="let project">
                    @for (collection of project; track collection) {
                      <div>
                        {{ collection.documentCount | number: '1.0' }}
                      </div>
                    }
                  </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="activeUseTableColumns; sticky: true"></tr>
                <tr mat-row *matRowDef="let row; columns: activeUseTableColumns"></tr>
                <tr class="xi-table__no-table-data" *matNoDataRow>
                  <td class="mat-cell" [attr.colspan]="activeUseTableColumns.length">
                    <div>
                      <div class="xi-table__no-results-icon"><xi-svg svg="no"></xi-svg></div>
                      <p class="xi-table__no-results-text">
                        {{ t('not-active-field') }}
                      </p>
                    </div>
                  </td>
                </tr>
              </table>
            </div>
          </section>
        }
        @if (aliasesTableData.data?.length) {
          <section class="field-view__card">
            <div class="field-view__section-header">
              <h5>{{ t('aliases') }}</h5>
            </div>
            <div class="field-view__table_wrapper">
              <table mat-table [dataSource]="aliasesTableData" class="xi-table__table">
                <ng-container matColumnDef="fieldLabel">
                  <th mat-header-cell *matHeaderCellDef>
                    <div class="xi-table__table-header-content">
                      <div class="xi-table__table-header-border"></div>
                      <span>{{ t('name') }}</span>
                    </div>
                  </th>
                  <td mat-cell *matCellDef="let field">{{ field.label }}</td>
                </ng-container>
                <ng-container matColumnDef="isFilterable">
                  <th mat-header-cell *matHeaderCellDef>
                    <div class="xi-table__table-header-content">
                      <div class="xi-table__table-header-border"></div>
                      <span>{{ t('filterable') }}</span>
                    </div>
                  </th>
                  <td mat-cell *matCellDef="let field">
                    {{ field.metadata?.filterable ? t('yes') : t('no') }}
                  </td>
                </ng-container>
                <ng-container matColumnDef="isPII">
                  <th mat-header-cell *matHeaderCellDef>
                    <div class="xi-table__table-header-content">
                      <div class="xi-table__table-header-border"></div>
                      <span>{{ t('contains-pii') }}</span>
                    </div>
                  </th>
                  <td mat-cell *matCellDef="let field">
                    {{ field.metadata?.redacted ? t('yes') : t('no') }}
                  </td>
                </ng-container>
                <ng-container matColumnDef="fieldName">
                  <th mat-header-cell *matHeaderCellDef>
                    <div class="xi-table__table-header-content">
                      <div class="xi-table__table-header-border"></div>
                      <span>{{ t('internal-name') }}</span>
                    </div>
                  </th>
                  <td mat-cell *matCellDef="let field">{{ field.fieldName }}</td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="aliasesTableColumns; sticky: true"></tr>
                <tr mat-row *matRowDef="let row; columns: aliasesTableColumns"></tr>
              </table>
            </div>
            @if (!isReadOnly) {
              <xi-fab
                icon="fab_plus"
                buttonSize="normal"
                color="orca"
                [disabled]="isLocked"
                [tooltip]="{ text: t('add-new-alias') }"
                (onClick)="onShowNewMetadataFieldModal()"
              ></xi-fab>
            }
          </section>
        }
        <div class="field-view__field-info">
          <div class="field-view__details-item">
            <p>{{ t('id') }}: {{ field.id }}</p>
            <xi-icon-button
              (click)="onCopyDetail(field.id)"
              [tooltip]="{ text: t('copy') }"
              color="CX"
              icon="copy"
            ></xi-icon-button>
          </div>
          <div class="field-view__details-item">
            <p>{{ t('internal-name') }}: {{ field.fieldName }}</p>
            <xi-icon-button
              (click)="onCopyDetail(field.fieldName)"
              [tooltip]="{ text: t('copy') }"
              color="CX"
              icon="copy"
            ></xi-icon-button>
          </div>
          @if (field.externalId) {
            <div class="field-view__details-item">
              <p>{{ t('external-name') }}: {{ field.externalId }}</p>
              <xi-icon-button
                (click)="onCopyDetail(field.externalId)"
                [tooltip]="{ text: t('copy') }"
                color="CX"
                icon="copy"
              ></xi-icon-button>
            </div>
          }
        </div>
      </form>
    }
  </div>

  @if (shouldShowSaveBar) {
    <div class="field-view__bottom-save-bar">
      <div class="field-view__bottom-save-bar-actions">
        <div>
          <xi-icon icon="warning" color="warning"></xi-icon>
          <span class="field-view__bottom-save-bar-warning">{{ t('unsaved-changes') }}</span>
        </div>
        <xi-default-button
          text="{{ t('revert-change') }}"
          fill="text"
          (onClick)="onRevert()"
        ></xi-default-button>
        <div class="xi-loader-button-container">
          <xi-default-button
            text="{{ t('save') }}"
            [disabled]="fieldForm.invalid || (bucketForm && !bucketForm.valid)"
            (onClick)="onSave()"
          ></xi-default-button>
          @if (isSaving) {
            <div class="xi-loader-container__inline-overlay">
              <xi-animation
                [height]="75"
                [width]="75"
                [loop]="true"
                animation="cx_loader"
              ></xi-animation>
            </div>
          }
        </div>
      </div>
    </div>
  }
</ng-container>

@if (showNewMetadataFieldModal) {
  <app-lex-xi-modal
    [isVisible]="showNewMetadataFieldModal"
    [canBackdropClose]="false"
    maxWidth="550px"
    (close)="onNewMetadataFieldModalClosed()"
  >
    <ng-template appXIModalContent>
      @if (showNewMetadataFieldModal) {
        <app-field-create-modal
          [fields]="allFields"
          [preSelectedField]="field"
          (modalClosed)="onNewMetadataFieldModalClosed($event)"
        ></app-field-create-modal>
      }
    </ng-template>
  </app-lex-xi-modal>
}
